import { PartiallyRequired } from '@thyme/libs/src/typing/partial'
import { IconifyIcons } from '@thyme/nashville/src/types/icons'
import { Timestamps } from '@/api/global/types/timestamps'
import { Direction, SpeakingWithType } from './shared'

// ENUMS --------------------
// Extends the shared Direction with call-only values (e.g. transfer)
// to align with the backend's inheritance pattern
export enum CallDirection {
  outbound = Direction.outbound,
  inbound = Direction.inbound,
  transfer = 'TRANSFER',
}

export enum Disposition {
  ANSWERED = 'ANSWERED',
  ANSWERED_REQUESTED_CALLBACK = 'ANSWERED_REQUESTED_CALLBACK',
  LEFT_VOICEMAIL = 'LEFT_VOICEMAIL',
  VOICEMAIL_FULL = 'VOICEMAIL_FULL',
  NUMBER_INOPERABLE = 'NUMBER_INOPERABLE',
  WRONG_NUMBER = 'WRONG_NUMBER',
  NO_ANSWER = 'NO_ANSWER',
  CONNECTIVITY_ISSUES = 'CONNECTIVITY_ISSUES',
  DO_NOT_CALL_LIST = 'DO_NOT_CALL_LIST',
  MEMBER_HUNG_UP = 'MEMBER_HUNG_UP',
  // Distinct from LEFT_VOICEMAIL, which is the outbound direction (we left the
  // member a voicemail). This one is inbound: the member left us one.
  RECEIVED_VOICEMAIL = 'RECEIVED_VOICEMAIL',
}

// TYPES --------------------
export type CallDisposition = {
  callDispositionId?: string
  communicationId: string
  direction: CallDirection
  disposition: Disposition
  isIdentityVerified: boolean
  usedTranslator: boolean
  speakingWithPersonId: string | null // this is an Entity id, not a Person id
  speakingWithType: SpeakingWithType | null
} & Timestamps

export type DirtyCallDisposition = PartiallyRequired<
  CallDisposition,
  'communicationId' | 'disposition' | 'direction'
>

export type CreateCallDisposition = Omit<
  DirtyCallDisposition,
  'communicationId'
>

// CONSTANTS --------------------
// Key order drives the call result dropdown options, so it is load-bearing.
export const callDispositionDisplayLookup: Record<Disposition, string> = {
  [Disposition.ANSWERED]: 'Answered',
  [Disposition.ANSWERED_REQUESTED_CALLBACK]: 'Answered - Requested Callback',
  [Disposition.LEFT_VOICEMAIL]: 'Left Voicemail',
  [Disposition.VOICEMAIL_FULL]: 'Voicemail Full',
  [Disposition.NUMBER_INOPERABLE]: 'Number Inoperable',
  [Disposition.WRONG_NUMBER]: 'Wrong Number',
  [Disposition.NO_ANSWER]: 'Did Not Answer - No Voicemail',
  [Disposition.CONNECTIVITY_ISSUES]: 'Connectivity Issues',
  [Disposition.DO_NOT_CALL_LIST]: 'Do Not Call List',
  [Disposition.MEMBER_HUNG_UP]: 'Member Hung Up',
  [Disposition.RECEIVED_VOICEMAIL]: 'Received Voicemail',
}

export const callTypeDisplayLookupV2 = {
  inbound: 'Inbound Call from',
  outbound: 'Outbound Call to',
  transfer: 'Transfer Call with',
  plannedCall: 'Scheduled Call to',
  text: 'Text Conversation',
} as { [key: string]: string }

export const callDirectionIconLookup: Record<CallDirection, string> = {
  [CallDirection.outbound]: IconifyIcons.phoneOutbound,
  [CallDirection.inbound]: IconifyIcons.phoneInbound,
  [CallDirection.transfer]: IconifyIcons.phoneTransfer,
}
